import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import { Phone as PhoneIcon, Sms as SmsIcon } from '@mui/icons-material';
import { signInWithPhoneNumber, PhoneAuthProvider, linkWithCredential, RecaptchaVerifier } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const PhoneVerification = ({ open, onClose, onSuccess }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);

    // 悪用対策: セッション内制限
    const [sessionAttempts, setSessionAttempts] = useState(0);
    const [lastAttemptTime, setLastAttemptTime] = useState(null);
    const [dailyAttempts, setDailyAttempts] = useState(0);

    const recaptchaRef = useRef(null);
    const { currentUser } = useAuth();
    const { showNotification } = useNotification();

    const steps = ['電話番号入力', '認証コード入力'];

    // 悪用対策: 制限値の定義
    const LIMITS = {
        MAX_SESSION_ATTEMPTS: 3,        // セッション内最大試行回数
        MAX_DAILY_ATTEMPTS: 5,          // 1日の最大試行回数
        MIN_RETRY_INTERVAL: 120,        // 最小再試行間隔（秒）
        SUSPICIOUS_THRESHOLD: 2         // 疑わしい行動の閾値
    };

    // 悪用対策: ローカルストレージから制限情報を取得
    useEffect(() => {
        const today = new Date().toDateString();
        const storedData = localStorage.getItem('phoneAuthLimits');

        if (storedData) {
            try {
                const data = JSON.parse(storedData);
                if (data.date === today) {
                    setDailyAttempts(data.attempts || 0);
                } else {
                    // 新しい日なのでリセット
                    localStorage.setItem('phoneAuthLimits', JSON.stringify({
                        date: today,
                        attempts: 0
                    }));
                }
            } catch (error) {
                console.error('制限データの読み込みエラー:', error);
            }
        }
    }, []);

    // 悪用対策: 制限チェック関数
    const checkLimits = () => {
        const now = Date.now();

        // セッション内試行回数チェック
        if (sessionAttempts >= LIMITS.MAX_SESSION_ATTEMPTS) {
            return {
                allowed: false,
                reason: `セッション内の試行回数が上限（${LIMITS.MAX_SESSION_ATTEMPTS}回）に達しました。ページを再読み込みしてください。`
            };
        }

        // 1日の試行回数チェック
        if (dailyAttempts >= LIMITS.MAX_DAILY_ATTEMPTS) {
            return {
                allowed: false,
                reason: `本日の試行回数が上限（${LIMITS.MAX_DAILY_ATTEMPTS}回）に達しました。明日再試行してください。`
            };
        }

        // 最小再試行間隔チェック
        if (lastAttemptTime && (now - lastAttemptTime) < LIMITS.MIN_RETRY_INTERVAL * 1000) {
            const remainingTime = Math.ceil((LIMITS.MIN_RETRY_INTERVAL * 1000 - (now - lastAttemptTime)) / 1000);
            return {
                allowed: false,
                reason: `再試行まで${remainingTime}秒お待ちください。`
            };
        }

        return { allowed: true };
    };

    // 悪用対策: 試行回数を記録
    const recordAttempt = () => {
        const now = Date.now();
        const today = new Date().toDateString();

        setSessionAttempts(prev => prev + 1);
        setLastAttemptTime(now);

        const newDailyAttempts = dailyAttempts + 1;
        setDailyAttempts(newDailyAttempts);

        // ローカルストレージに保存
        localStorage.setItem('phoneAuthLimits', JSON.stringify({
            date: today,
            attempts: newDailyAttempts
        }));

        // 疑わしい行動の検出
        if (sessionAttempts >= LIMITS.SUSPICIOUS_THRESHOLD) {
            console.warn('疑わしい電話番号認証の使用パターンを検出しました');
            // 必要に応じてサーバーに報告
        }
    };

    // カウントダウンタイマー
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    // reCAPTCHAの初期化（軽量版）
    const initializeRecaptcha = () => {
        if (recaptchaRef.current) {
            return recaptchaRef.current;
        }

        try {
            console.log('reCAPTCHA初期化開始');

            recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
                size: 'invisible',
                callback: () => console.log('reCAPTCHA solved'),
                'expired-callback': () => {
                    console.log('reCAPTCHA expired');
                    setError('認証の有効期限が切れました。再試行してください。');
                }
            });

            console.log('reCAPTCHA初期化完了');
            return recaptchaRef.current;
        } catch (error) {
            console.error('reCAPTCHA初期化エラー:', error);
            setError('認証システムの初期化に失敗しました。ページをリロードしてください。');
            return null;
        }
    };

    // reCAPTCHAのクリーンアップ
    const cleanupRecaptcha = () => {
        if (recaptchaRef.current) {
            try {
                recaptchaRef.current.clear();
            } catch (error) {
                console.error('reCAPTCHAクリーンアップエラー:', error);
            }
            recaptchaRef.current = null;
        }
    };

    // ダイアログが閉じられた時のクリーンアップ
    useEffect(() => {
        if (!open) {
            setActiveStep(0);
            setPhoneNumber('');
            setVerificationCode('');
            setConfirmationResult(null);
            setError('');
            setCountdown(0);
            cleanupRecaptcha();
        }
    }, [open]);

    // コンポーネントのアンマウント時のクリーンアップ
    useEffect(() => {
        return () => {
            cleanupRecaptcha();
        };
    }, []);

    // 電話番号のフォーマット
    const formatPhoneNumber = (value) => {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.startsWith('0')) {
            return `+81${cleaned.substring(1)}`;
        } else if (cleaned.startsWith('81')) {
            return `+${cleaned}`;
        } else if (!cleaned.startsWith('+')) {
            return `+81${cleaned}`;
        }
        return value;
    };

    // SMS送信（制限チェック付き）
    const sendVerificationCode = async () => {
        if (!phoneNumber.trim()) {
            setError('電話番号を入力してください');
            return;
        }

        // 悪用対策: 制限チェック
        const limitCheck = checkLimits();
        if (!limitCheck.allowed) {
            setError(limitCheck.reason);
            return;
        }

        setLoading(true);
        setError('');

        try {
            // 悪用対策: 試行回数を記録
            recordAttempt();

            const recaptchaVerifier = initializeRecaptcha();
            if (!recaptchaVerifier) {
                throw new Error('認証システムの初期化に失敗しました');
            }

            const formattedPhone = formatPhoneNumber(phoneNumber);
            console.log('送信する電話番号:', formattedPhone);

            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);

            setConfirmationResult(confirmation);
            setActiveStep(1);
            setCountdown(60);
            showNotification('認証コードを送信しました', 'success');
        } catch (error) {
            console.error('SMS送信エラー:', error);
            let errorMessage = 'SMS送信に失敗しました';

            switch (error.code) {
                case 'auth/invalid-phone-number':
                    errorMessage = '無効な電話番号です。日本の電話番号を入力してください。';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'リクエストが多すぎます。しばらく待ってから再試行してください。';
                    break;
                case 'auth/quota-exceeded':
                    errorMessage = 'SMS送信の上限に達しました。しばらく待ってから再試行してください。';
                    break;
                case 'auth/captcha-check-failed':
                    errorMessage = 'セキュリティ認証に失敗しました。再試行してください。';
                    cleanupRecaptcha();
                    break;
                default:
                    errorMessage = `SMS送信に失敗しました: ${error.message}`;
                    cleanupRecaptcha();
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // 認証コード確認
    const verifyCode = async () => {
        if (!verificationCode.trim()) {
            setError('認証コードを入力してください');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const credential = PhoneAuthProvider.credential(
                confirmationResult.verificationId,
                verificationCode
            );

            if (currentUser) {
                try {
                    await linkWithCredential(auth.currentUser, credential);
                    console.log('電話番号認証リンク成功');
                } catch (linkError) {
                    console.warn('リンクエラー:', linkError);
                    // 特定のエラーは無視して続行
                    if (!['auth/provider-already-linked', 'auth/credential-already-in-use', 'auth/account-exists-with-different-credential'].includes(linkError.code)) {
                        throw linkError;
                    }
                }

                // サーバーに認証状態を更新
                const token = await auth.currentUser.getIdToken(true);
                const response = await fetch('/api/users/update-phone-verification', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        phoneNumber: formatPhoneNumber(phoneNumber),
                        phoneVerified: true
                    })
                });

                if (!response.ok) {
                    throw new Error('認証状態の更新に失敗しました');
                }

                showNotification('電話番号認証が完了しました', 'success');

                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            console.error('認証コード確認エラー:', error);
            let errorMessage = '認証コードが正しくありません';

            switch (error.code) {
                case 'auth/invalid-verification-code':
                    errorMessage = '認証コードが正しくありません';
                    break;
                case 'auth/code-expired':
                    errorMessage = '認証コードの有効期限が切れました';
                    break;
                default:
                    errorMessage = error.message || '認証に失敗しました';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // 再送信
    const resendCode = async () => {
        setActiveStep(0);
        setVerificationCode('');
        setConfirmationResult(null);
        setError('');
        setCountdown(0);
        cleanupRecaptcha();
    };

    const handleBack = () => {
        setActiveStep(0);
        setVerificationCode('');
        setError('');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    電話番号認証
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {activeStep === 0 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            日本の携帯電話番号を入力してください。SMS認証コードが送信されます。
                        </Typography>

                        <TextField
                            fullWidth
                            label="電話番号"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="090-1234-5678"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneIcon color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {/* reCAPTCHAコンテナ */}
                        <div id="recaptcha-container"></div>
                    </Box>
                )}

                {activeStep === 1 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {phoneNumber} に送信された6桁の認証コードを入力してください。
                        </Typography>

                        <TextField
                            fullWidth
                            label="認証コード"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="123456"
                            inputProps={{ maxLength: 6 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SmsIcon color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />

                        {countdown > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                再送信まで {countdown} 秒
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1 }}>
                {activeStep === 1 && (
                    <Button onClick={handleBack} disabled={loading}>
                        戻る
                    </Button>
                )}

                {activeStep === 1 && countdown === 0 && (
                    <Button onClick={resendCode} disabled={loading}>
                        再送信
                    </Button>
                )}

                <Button onClick={onClose} disabled={loading}>
                    キャンセル
                </Button>

                <Button
                    variant="contained"
                    onClick={activeStep === 0 ? sendVerificationCode : verifyCode}
                    disabled={loading || (activeStep === 0 ? !phoneNumber.trim() : !verificationCode.trim())}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {loading ? '処理中...' : activeStep === 0 ? 'SMS送信' : '認証'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PhoneVerification; 